import { apiRoutes } from "../../shared/api";
import type {
  ApiFailure,
  ApiResponse,
  ResultJobStatusResponse,
  ResultJobSubmitResponse,
  ResultLookupRequest,
  ResultLookupResponse,
  TokenResponse,
  TurnstileSessionRequest,
} from "../../shared/types";

const REQUEST_TIMEOUT_MS = 90_000;
const SESSION_ID_KEY = "moed-results:session-id";
const DEFAULT_ERROR_MESSAGE = "حدث خطأ في الطلب. حاول مرة أخرى.";

type LooseApiFailure = ApiFailure | {
  success: false;
  message?: string;
  code?: string;
  requestId?: string;
};

export class ApiClientError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly status: number;
  readonly retryAfter?: string;

  constructor(message: string, code: string, status: number, requestId?: string, retryAfter?: string) {
    super(message);
    this.name = "ApiClientError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  const retryAfter = response.headers.get("retry-after") ?? undefined;
  let body: ApiResponse<T> | undefined;
  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError("رد غير مفهوم من الخادم.", "INVALID_RESPONSE", response.status);
  }

  if (!body.success) {
    const failure = body as LooseApiFailure;
    const hasErrorEnvelope = "error" in failure && Boolean(failure.error);
    const looseMessage = "message" in failure ? failure.message : undefined;
    const looseCode = "code" in failure ? failure.code : undefined;
    const looseRequestId = "requestId" in failure ? failure.requestId : undefined;
    throw new ApiClientError(
      hasErrorEnvelope ? failure.error.message : (looseMessage ?? DEFAULT_ERROR_MESSAGE),
      hasErrorEnvelope ? failure.error.code : (looseCode ?? "RESULT_LOOKUP_FAILED"),
      response.status,
      hasErrorEnvelope ? failure.error.requestId : looseRequestId,
      retryAfter,
    );
  }

  return body.data;
};

const requestJson = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const sessionId = getSessionId();
  try {
    const response = await fetch(path, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(sessionId ? { "x-session-id": sessionId } : {}),
        ...(init.headers ?? {}),
      },
      signal: controller.signal,
    });
    return await parseResponse<T>(response);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiClientError("استغرق الطلب وقتا طويلا. حاول مرة أخرى بعد قليل.", "REQUEST_TIMEOUT", 408);
    }
    if (error instanceof TypeError) {
      throw new ApiClientError("لا يوجد اتصال بالخدمة الآن. تأكد أن السيرفر يعمل ثم حاول مرة أخرى.", "NETWORK_ERROR", 0);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getSessionId = (): string | undefined => {
  try {
    const existing = window.localStorage.getItem(SESSION_ID_KEY)?.trim();
    if (existing) {
      return existing;
    }

    const next = crypto.randomUUID();
    window.localStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch {
    return undefined;
  }
};

export const apiClient = {
  createTurnstileSession: (payload: TurnstileSessionRequest) =>
    requestJson<TokenResponse>(apiRoutes.turnstileSession, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  lookupResult: (payload: ResultLookupRequest) =>
    requestJson<ResultLookupResponse | ResultJobSubmitResponse>(apiRoutes.results, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getResultJob: (jobId: string) => requestJson<ResultJobStatusResponse>(apiRoutes.resultJob(jobId)),
};
